import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import Bill from './Bill';
import Booking from './Booking';
import Account from './Account';
import Members from './Members';
import MarqueeImageComponent from '../MembersBirthdays';
import logo from '../../Images/logo-4.png';
import { BASEAPIURL, token } from '../../env';
import SportsBooking from './SportsBooking';
import CartPage from './CartPage';
import { formatDate } from '../commonFunctionlity';

const Dashboard = () => {
  const [section, setSection] = useState('home');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [memberData, setMemberData] = useState({});
  const [memberImage, setMemberImage] = useState(logo);

  const navigate = useNavigate();

  // Handle logout
  useEffect(() => {

    if (section.toLowerCase() === 'logout') {
      sessionStorage.clear();
      navigate('/login', { replace: true });
    }
  }, [section, navigate]);

  const fetchMemberData = useCallback((memberID, SessionID) => {
    const myHeaders = new Headers();
    myHeaders.append('Content-Type', 'application/json');

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify({ tokenData: token }),
      redirect: 'follow',
    };

    fetch(`${BASEAPIURL}/MemberByID?MainID=${memberID}`, requestOptions)
      .then((res) => {
        if (!res.ok) throw new Error(`Error fetching member data: ${res.status}`);
        return res.json();
      })
      .then((result) => {
        const data = result?.data || {};
        setMemberData(data);
        if (data?.ImageBase64 && data.ImageBase64.startsWith('data:image')) {
          setMemberImage(data.ImageBase64);
        } else {
          setMemberImage(logo); // fallback
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError(err.message || 'Failed to fetch member data.');
        setLoading(false);
      });
  }, []);

  // Check session and fetch data
  useEffect(() => {
    let retries = 0;
    const maxRetries = 1;
    const retryDelay = 1000; // 1 second

    const checkSessionAndFetch = () => {
      const memberID = sessionStorage.getItem('MemberID');


      if (!memberID ) {
        if (retries < maxRetries) {
          retries++;
          console.warn(`Retrying session check... (${retries}/${maxRetries})`);
          setTimeout(checkSessionAndFetch, retryDelay);
        } else {
          setLoading(false);
          alert('Login failed: Session expired or invalid credentials.');
          navigate('/login', { replace: true });
        }
        return;
      }

      // Clear retries on success
      fetchMemberData(memberID);
    };

    checkSessionAndFetch();

    // Cleanup: avoid memory leaks if component unmounts
    return () => {
      // No ongoing timeout to clear, but good to note
    };
  }, [fetchMemberData, navigate]);

  const renderSection = () => {
    switch (section) {
      case 'Account':
        return <Account />;
      case 'Payments':
        return <Bill />;
      case 'Booking':
        return <Booking />;
      case 'Sports Booking':
        return <SportsBooking />;
      case 'Wishlist':
        return <MarqueeImageComponent />;
      case 'Members':
        return <Members />;
      case 'Cart':
        return <CartPage />;
      case 'Logout':
        return <div className="text-gray-500">Logging out...</div>;
      default:
        return <Account />;
    }
  };

  formatDate()

  // Sidebar items
  const sidebarItems = [
    { key: 'Account', label: 'Account', icon: '💻' },
    { key: 'Payments', label: 'Payments', icon: '🧾' },
    { key: 'Booking', label: 'Booking', icon: '🛎️' },
    { key: 'Sports Booking', label: 'Sports Booking', icon: '🏃‍♂️' },
    { key: 'Members', label: 'Members', icon: '👥' },
    { key: 'Cart', label: 'Cart', icon: '🛒' },
    { key: 'Wishlist', label: 'Birthdays', icon: '🎂' }, // renamed for clarity
    { key: 'Logout', label: 'Logout', icon: '🚪' },
  ];

  if (loading) return <div className="text-center py-10">Loading profile...</div>;
  if (error)
    return (
      <div className="text-center py-10 text-red-500">
        {error}
        <br />
        <button
          onClick={() => navigate('/login')}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded"
        >
          Go to Login
        </button>
      </div>
    );

  return (
    <div className="flex min-h-screen container-fluid bg-gray-100">
      {/* Sidebar */}
      <div className="w-64 flex-shrink-0">
        <Sidebar section={section} onSelect={setSection} items={sidebarItems} />
      </div>

      {/* Main Content */}
      <main className="flex-grow p-2 sm:p-10 transition-all duration-300">
        {/* Profile Header */}
        <div
          className="rounded-5"
          style={{
            minHeight: '50px',
            marginTop: '50px',
            background:
              'linear-gradient(90deg, rgba(200, 182, 166, 1) 17%, rgba(217, 164, 117, 1) 66%, rgba(252, 176, 69, 1) 100%)',
          }}
        >
          <div className="row align-items-center">
            {/* Profile Image */}
            <div className="col-12 col-md-4 text-center">
              <img
                src={memberImage}
                className="rounded-circle border border-4"
                style={{
                  width: '150px',
                  height: '150px',
                  objectFit: 'cover',
                  marginTop: '-70px',
                  backgroundColor: 'white',
                }}
                alt="Member"
                onError={(e) => {
                  e.target.src = logo;
                }}
              />
            </div>

            {/* Name */}
            <div className="col-12 col-md-4 text-center text-white mt-3">
              <h3>{memberData.MemName || 'Guest'}</h3>
            </div>

            {/* Membership Info */}
            <div className="col-12 col-md-4 text-center text-white mt-3">
              <h5>Member since:  {formatDate(memberData.MemRegDt) || 'N/A'}</h5>
              <h5>Membership Type:  {memberData.MemType || 'N/A'}</h5>
              <h5 >Membership No:  {memberData.MemMSNO || 'N/A'}</h5>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div
          className="bg-white shadow-xl rounded-2xl p-6 sm:p-8 min-h-[85vh]"
          style={{ marginTop: '01px' }}
        >
          {renderSection()}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;